import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { IAgent, IGrantor, IFuturePowSetting } from "../types";
import { recommendedSettings, settingIds, extendOptions, settingCategories } from "../constants";

interface ITwoFuturePowBundle {
  firstDocumentIdentifier: string;
  secondDocumentIdentifier: string;
  firstDocument: {
    grantor: IGrantor;
    agents: IAgent[];
    settings: IFuturePowSetting[];
  };
  secondDocument: {
    grantor: IGrantor;
    agents: IAgent[];
    settings: IFuturePowSetting[];
  };
}

type TTwoFuturePowDocumentKey = "firstDocument" | "secondDocument";

const initialState: ITwoFuturePowBundle = {
  firstDocumentIdentifier: "",
  secondDocumentIdentifier: "",
  firstDocument: {
    grantor: {
      fullname: "",
      ssn: "",
    },
    agents: [],
    settings: [
      { id: settingIds.extend, value: extendOptions.economic_personal, category: settingCategories.general },
      ...recommendedSettings,
    ],
  },
  secondDocument: {
    grantor: {
      fullname: "",
      ssn: "",
    },
    agents: [],
    settings: [
      { id: settingIds.extend, value: extendOptions.economic_personal, category: settingCategories.general },
      ...recommendedSettings,
    ],
  },
};

const twoFuturePowBundleSlice = createSlice({
  name: "twoFuturePowBundle",
  initialState,
  reducers: {
    setGrantorData(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; data: Partial<IGrantor> }>) {
      const { document, data } = action.payload;
      Object.assign(state[document].grantor, data);
    },
    setAgentsData(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; data: IAgent[] }>) {
      const { document, data } = action.payload;
      state[document].agents = data;
    },
    setSettingById(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; setting: IFuturePowSetting }>) {
      const { document, setting: payloadSetting } = action.payload;
      const targetDocument = state[document];
      const setting = targetDocument.settings.find((setting) => setting.id === payloadSetting.id);
      if (setting) {
        setting.value = payloadSetting.value;
      } else {
        targetDocument.settings.push(payloadSetting);
      }
    },
    setSettings(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; settings: IFuturePowSetting[] }>) {
      const { document, settings: payloadSettings } = action.payload;
      const targetDocument = state[document];
      const incomingItems = new Map(payloadSettings.map((setting) => [setting.id, setting]));
      const updated = targetDocument.settings.map((setting) => incomingItems.get(setting.id) ?? setting);
      targetDocument.settings = [
        ...updated,
        ...payloadSettings.filter((setting) => !targetDocument.settings.some((existing) => existing.id === setting.id)),
      ];
    },
    removeSettingById(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; id: string }>) {
      const { document, id } = action.payload;
      state[document].settings = state[document].settings.filter((setting) => setting.id !== id);
    },
    setDocumentIdentifier(state, action: PayloadAction<{ document: TTwoFuturePowDocumentKey; identifier: string }>) {
      const key = action.payload.document === "firstDocument" ? "firstDocumentIdentifier" : "secondDocumentIdentifier";
      state[key] = action.payload.identifier;
    },
  },
});

export const { setGrantorData, setAgentsData, setSettingById, setSettings, removeSettingById, setDocumentIdentifier } =
  twoFuturePowBundleSlice.actions;
export type { TTwoFuturePowDocumentKey };
export default twoFuturePowBundleSlice.reducer;
